// The out-of-date mark (P1 of documentation/pr-plans/insights-simplify-plan.md, section 7), drawn by
// <x-insights.out-of-date> at the top of every screen the Insights page keeps open: each activity, and
// Stats. A write saved on ANOTHER screen (WRITTEN_EVENT, insights/writes.js) shows it, since this screen
// was read before that write; pressing it reads this screen again through its own Livewire method
// (`reload`). It goes once the screen has read again, by the press or by one of the screen's own reload
// listeners, each of which sends READ_EVENT with its screen. Each screen's owner swaps those listeners for
// this mark, and P3 makes the writes behind it fast (section 7).

import { WRITTEN_EVENT } from "./writes.js";

/** Sent by a screen's Livewire component each time it reads its figures again: {screen} (InsightsWrites::READ_EVENT). */
export const READ_EVENT = "insights-screen-read";

export { WRITTEN_EVENT };

/**
 * @param {{screen: string, reload: string, focus: string}} config  the screen's key (the writes module's
 *        screenOf()), the Livewire method that reads it again, and the id of the element focus goes to
 *        when the mark leaves while it held focus (the screen's heading, or its pane)
 */
export default function InsightsOutOfDate({ screen, reload, focus }) {
    return {
        ood_shown: false,
        ood_reading: false,

        ood_written(detail) {
            if ((detail?.screen ?? "") !== screen) {
                this.ood_shown = true;
            }
        },

        ood_read(detail) {
            if (detail?.screen !== screen) {
                return;
            }

            const heldFocus = this.$el.contains(document.activeElement);

            this.ood_shown = false;
            this.ood_reading = false;

            // The mark and its button leave the page, so focus would fall to the document: it goes to the
            // screen's heading or pane instead, which the reader reads on from (2.4.3).
            if (heldFocus) {
                document.getElementById(focus)?.focus();
            }
        },

        // Convention exception: settling a Livewire call on failure.
        // Livewire's $wire promise resolves on success only (insights-activity-table.js' callWire() says
        // why), so the press settles on its own commit's succeed or fail hook instead, matched by the
        // component and the method, and the button never stays busy after a failed read.
        ood_reload() {
            if (this.ood_reading) {
                return;
            }

            this.ood_reading = true;

            const id = this.$wire.$id;
            const livewire = typeof window !== "undefined" ? window.Livewire : null;
            let unhook = () => {};

            if (livewire && typeof livewire.hook === "function") {
                unhook = livewire.hook("commit", ({ component, commit, succeed, fail }) => {
                    if (component?.id !== id || !(commit.calls || []).some((call) => call.method === reload)) {
                        return;
                    }

                    unhook();
                    succeed(() => {
                        this.ood_reading = false;
                    });
                    fail(() => {
                        this.ood_reading = false;
                    });
                });
            }

            this.$wire.call(reload);
        },
    };
}
